import type { ChangeEvent } from "react";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { inject, injectable } from "tsyringe";
import type { InputOnChangeData } from "@/components/Input/Input";
import {
  MAX_PRODUCT_PHOTOS,
  normalizeProductPhotoState,
  removeProductPhoto,
} from "@/domain/product-photos";
import {
  extractQrPayload,
  normalizeQrPayloadInput,
  qrScanErrorToMessage,
  validateQrPayload,
} from "@/domain/qr-payload";
import {
  type BoxListItem,
  type IWarehouseApi,
  QrBindingConflictError,
  WAREHOUSE_API_TOKEN,
} from "@/api/WarehouseApi";
import { ViewModel } from "@orderofchaos/mvvm";
import type { Product } from "@/stores/Product";
import { Measures } from "@/types";
import { normalizeTypeTags } from "@/utils/typeTags";
import {
  formatBoxReferenceInputValue,
  formatBoxReferenceLabel,
} from "@/utils/boxReference";
import {
  type INavigationStore,
  NAVIGATION_STORE_TOKEN,
  Screens,
} from "@/stores/navigation";
import type { IStore } from "@/stores/store";
import { STORE_TOKEN } from "@/stores/store";
import {
  loadEditProductReferenceDataOperation,
  resetEditProductFromProductOperation,
  setPhotoFromFileOperation,
  submitEditProductOperation,
  type EditProductWorkflowState,
} from "./viewmodel/operations";

export interface EditProductPageViewProps {
  product: Product;
}

@injectable()
export class EditProductPageViewModel extends ViewModel<
  unknown,
  EditProductPageViewProps
> {
  @observable
  name = "";

  @observable
  description = "";

  @observable
  photo = "";

  @observable.shallow
  photos: string[] = [];

  @observable
  activePhotoIndex = 0;

  @observable
  typeTags: string[] = [];

  @observable
  typeTagInput = "";

  @observable
  parentId = "";

  @observable
  manufacturingDate = "";

  @observable
  expiryDate = "";

  @observable
  measure: Measures = Measures.quantity;

  @observable
  quantityInput = "1";

  @observable.shallow
  availableBoxes: BoxListItem[] = [];

  @observable.shallow
  typeTagSuggestions: string[] = [];

  @observable
  isReferenceLoading = false;

  @observable
  referenceError: string | null = null;

  @observable
  isPhotoLoading = false;

  @observable
  isSubmitting = false;

  @observable
  error: string | null = null;

  @observable
  boundQrPayload: string | null = null;

  @observable
  qrPayloadInput = "";

  @observable
  qrBindingError: string | null = null;

  @observable
  isQrScannerOpen = false;

  @observable
  isQrScanResolving = false;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(STORE_TOKEN) private readonly store: IStore,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();

    makeObservable(this);
  }

  @computed
  get product(): Product {
    return this.viewProps.product;
  }

  @computed
  get title(): string {
    return `редактируем ${this.name.trim() || "продукт"}`;
  }

  @computed
  get hasPhotoPreview(): boolean {
    return this.photos.length > 0 || this.photo.trim().length > 0;
  }

  @computed
  get normalizedPhotos(): string[] {
    return normalizeProductPhotoState({
      photo: this.photo,
      photos: this.photos,
    }).photos;
  }

  @computed
  get photoCounterLabel(): string {
    return `Фото: ${this.normalizedPhotos.length}/${MAX_PRODUCT_PHOTOS}`;
  }

  @computed
  get canAddMorePhotos(): boolean {
    return (
      this.normalizedPhotos.length < MAX_PRODUCT_PHOTOS &&
      !this.isSubmitting &&
      !this.isPhotoLoading
    );
  }

  @computed
  get canRemoveActivePhoto(): boolean {
    return this.normalizedPhotos.length > 0 && !this.isSubmitting;
  }

  @computed
  get canSubmit(): boolean {
    return !this.isSubmitting;
  }

  @computed
  get qrScannerDelay(): number | false {
    if (!this.isQrScannerOpen || this.isSubmitting || this.isQrScanResolving) {
      return false;
    }

    return 300;
  }

  @computed
  get parentIdSuggestions(): Array<{ id: string; label: string }> {
    return this.availableBoxes.map((box) => ({
      id: box.id,
      label: formatBoxReferenceLabel(box),
    }));
  }

  protected onViewMounted(): void {
    this.resetFromProduct();
    void this.loadBoundQrPayload();
    void this.loadReferenceData();
  }

  @action.bound
  onNameChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.name = data.value || event.target.value;
    this.error = null;
  }

  @action.bound
  onDescriptionChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.description = data.value || event.target.value;
    this.error = null;
  }

  @action.bound
  removePhoto(index: number): void {
    const nextState = removeProductPhoto(
      {
        photo: this.photo,
        photos: this.photos,
      },
      index,
    );
    this.photo = nextState.photo;
    this.photos = nextState.photos;
    this.activePhotoIndex = Math.max(
      0,
      Math.min(this.activePhotoIndex, this.photos.length - 1),
    );
    this.error = null;
  }

  @action.bound
  removeActivePhoto(): void {
    if (!this.canRemoveActivePhoto) {
      return;
    }

    this.removePhoto(this.activePhotoIndex);
  }

  @action.bound
  onPhotoSliderActiveIndexChange(index: number): void {
    this.activePhotoIndex = Math.max(
      0,
      Math.min(index, this.photos.length - 1),
    );
  }

  @action.bound
  async onPhotoFileChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    await setPhotoFromFileOperation({
      state: this.workflowState,
      event,
    });
    this.activePhotoIndex = 0;
  }

  @action.bound
  setTypeTagInput(value: string): void {
    this.typeTagInput = value;
    this.error = null;
  }

  @action.bound
  addTypeTags(tags: string[]): void {
    this.typeTags = normalizeTypeTags([...this.typeTags, ...tags]);
    this.error = null;
  }

  @action.bound
  removeTypeTag(tag: string): void {
    this.typeTags = this.typeTags.filter((item) => item !== tag);
    this.error = null;
  }

  @action.bound
  setParentIdInput(value: string): void {
    this.parentId = value;
    this.error = null;
  }

  @action.bound
  onManufacturingDateChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.manufacturingDate = data.value || event.target.value;
    this.error = null;
  }

  @action.bound
  setManufacturingDateValue(value: string): void {
    this.manufacturingDate = value;
    this.error = null;
  }

  @action.bound
  onExpiryDateChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.expiryDate = data.value || event.target.value;
    this.error = null;
  }

  @action.bound
  setExpiryDateValue(value: string): void {
    this.expiryDate = value;
    this.error = null;
  }

  @action.bound
  setMeasure(measure: Measures): void {
    this.measure = measure;
  }

  @action.bound
  onQuantityChange(
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void {
    this.quantityInput = data.value || event.target.value;
    this.error = null;
  }

  @action.bound
  toggleQrScanner(): void {
    if (this.isSubmitting) {
      return;
    }

    this.isQrScannerOpen = !this.isQrScannerOpen;
    this.qrBindingError = null;
  }

  @action.bound
  async onQrScan(rawData: string | null): Promise<void> {
    if (!rawData || !this.isQrScannerOpen || this.isQrScanResolving) {
      return;
    }

    const payload = extractQrPayload(rawData);
    if (!payload) {
      this.qrBindingError =
        "Сканируйте QR-код из партии печати (формат qr_...).";
      return;
    }

    runInAction(() => {
      this.isQrScanResolving = true;
      this.qrBindingError = null;
    });

    try {
      const existing = await this.api.resolveQrPayload(payload);
      if (existing && existing.entityId !== this.product.id) {
        runInAction(() => {
          this.qrPayloadInput = "";
          this.qrBindingError = this.formatQrConflictMessage(
            payload,
            existing.entityId,
          );
        });
        return;
      }

      runInAction(() => {
        this.qrPayloadInput = payload;
        this.qrBindingError = null;
        this.isQrScannerOpen = false;
      });
    } catch (error) {
      runInAction(() => {
        this.qrBindingError =
          error instanceof Error
            ? error.message
            : "Не удалось проверить QR-код";
      });
    } finally {
      runInAction(() => {
        this.isQrScanResolving = false;
      });
    }
  }

  @action.bound
  onQrScanError(error: unknown): void {
    this.qrBindingError = qrScanErrorToMessage(error);
  }

  @action.bound
  async loadReferenceData(): Promise<void> {
    await loadEditProductReferenceDataOperation({
      state: this.workflowState,
      api: this.api,
      formatParentInputValue: (value) => this.formatParentInputValue(value),
    });
  }

  @action.bound
  resetFromProduct(): void {
    resetEditProductFromProductOperation({
      state: this.workflowState,
      product: this.product,
      formatParentInputValue: (value) => this.formatParentInputValue(value),
    });
    const normalizedPhotoState = normalizeProductPhotoState({
      photo: this.photo,
      photos: this.photos,
    });
    this.photo = normalizedPhotoState.photo;
    this.photos = normalizedPhotoState.photos;
    this.activePhotoIndex = 0;
    this.boundQrPayload = null;
    this.qrPayloadInput = "";
    this.qrBindingError = null;
    this.isQrScannerOpen = false;
    this.isQrScanResolving = false;
    void this.loadBoundQrPayload();
  }

  @action.bound
  goBack(): void {
    this.navigationStore.setScreen(Screens.Product, this.product);
  }

  @action.bound
  async submit(): Promise<void> {
    const nextQrPayload = normalizeQrPayloadInput(this.qrPayloadInput);
    if (nextQrPayload && !validateQrPayload(nextQrPayload)) {
      this.qrBindingError = "Укажите QR в формате qr_...";
      return;
    }

    if (nextQrPayload) {
      const isQrAvailable = await this.ensureQrPayloadAvailable(
        nextQrPayload,
        this.product.id,
      );
      if (!isQrAvailable) {
        return;
      }
    }

    this.qrBindingError = null;

    await submitEditProductOperation({
      state: this.workflowState,
      api: this.api,
      store: this.store,
      navigationStore: this.navigationStore,
      product: this.product,
      onUpdated: async (product) => {
        if (!nextQrPayload) {
          return true;
        }

        try {
          const result = await this.api.bindQrPayload({
            entityId: product.id,
            payload: nextQrPayload,
          });
          runInAction(() => {
            this.boundQrPayload = result.payload;
            this.qrPayloadInput = "";
          });
          return true;
        } catch (error) {
          if (error instanceof QrBindingConflictError) {
            runInAction(() => {
              this.qrBindingError = this.formatQrConflictMessage(
                nextQrPayload,
                error.existingEntityId,
              );
            });
            return false;
          }

          runInAction(() => {
            this.qrBindingError =
              error instanceof Error
                ? error.message
                : "Не удалось привязать QR-код к товару";
          });
          return false;
        }
      },
    });
  }

  private async loadBoundQrPayload(): Promise<void> {
    try {
      const binding = await this.api.getQrBindingByEntity(this.product.id);
      runInAction(() => {
        this.boundQrPayload = binding?.payload ?? null;
      });
    } catch {
      runInAction(() => {
        this.boundQrPayload = null;
      });
    }
  }

  private async ensureQrPayloadAvailable(
    payload: string,
    allowEntityId: string | null,
  ): Promise<boolean> {
    try {
      const existing = await this.api.resolveQrPayload(payload);
      if (!existing || existing.entityId === allowEntityId) {
        return true;
      }

      this.qrBindingError = this.formatQrConflictMessage(
        payload,
        existing.entityId,
      );
      return false;
    } catch (error) {
      this.qrBindingError =
        error instanceof Error ? error.message : "Не удалось проверить QR-код";
      return false;
    }
  }

  private formatQrConflictMessage(
    payload: string,
    entityId: string | null,
  ): string {
    if (!entityId) {
      return `QR ${payload} уже используется другой сущностью.`;
    }

    return `QR ${payload} уже привязан к ${entityId}. Выберите другой код.`;
  }

  private get workflowState(): EditProductWorkflowState {
    return this;
  }

  private formatParentInputValue(value: string): string {
    return formatBoxReferenceInputValue(value, this.availableBoxes);
  }
}
